import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isDate } from 'date-fns';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import logger from '@widgets/logger';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { CHANNEL } from '@constants/channels';
import { alertActions, campaignsActions } from '@actions';
import { campaignsService } from '@services';
import { ModalType } from '@constants/modal';
import { MAX_LENGTH } from '@constants/campaigns';
import { handleApiError } from '@helpers/api';
import { testEditorLength } from '@helpers/text-editor';
import { CAMPAIGN_PLACEHOLDER_TOKENS } from '@constants/placeholders';
import ContentSource, { MESSAGE_TYPE } from './ContentSource';
import PrimaryFields from './PrimaryFields';
import ScheduledTimeField from './ScheduledTimeField';
import LabelsFields from './LabelsField';
import { Modal, Content } from './styles';

const EditModal = ({ modal, setModal, selectedCampaign, setSelectedCampaign, rowsPerPage, onDelete }) => {
    const { t } = useTranslation();

    const [contactsNumber, setContactsNumber] = useState(0);
    const [channelType, setChannelType] = useState(CHANNEL.SMS);
    const [messageType, setMessageType] = useState(MESSAGE_TYPE.MESSAGE);

    const contactsNumberRef = useRef(0);
    const selectedEmailTemplateRef = useRef(null);
    const selectedWhatsAppTemplateRef = useRef(null);

    const { data: labels } = useSelector(state => state.labels);
    const { data } = useSelector(state => state.campaigns);
    const channels = useSelector(state => state.channels.data);
    const user = useSelector(state => state.auth.user);
    const dispatch = useDispatch();

    const IS_EMAIL_CHANNEL = channelType === CHANNEL.EMAIL;
    const IS_WHATSAPP = channelType === CHANNEL.WHATSAPP;
    const IS_WHATSAPP_BUSINESS = channelType === CHANNEL.WHATSAPP_BUSINESS;

    const { data: libraryTemplates } = useSelector(state => state.campaignTemplates);

    const CampaignSchema = yup.object().shape({
        messageType: yup.number(),
        title: yup.string().required('validation.required'),
        channelId: yup.string().required('validation.required'),
        scheduledTime: yup.string().nullable().required('validation.required'),
        labels: yup.array().test('messages-quota', 'messages-quota', () => contactsNumberRef.current > 0),
        message: yup.string().when('messageType', {
            is: messageType =>
                (messageType === MESSAGE_TYPE.MESSAGE || messageType === MESSAGE_TYPE.COUPON) &&
                !IS_WHATSAPP_BUSINESS &&
                !IS_EMAIL_CHANNEL,
            then: schema =>
                schema.required('validation.required').test('text-editor-length', `validation.maxLength`, value => {
                    return testEditorLength(value, MAX_LENGTH[channelType], CAMPAIGN_PLACEHOLDER_TOKENS);
                }),
            otherwise: schema => schema.notRequired(),
        }),
        subject: yup.string().nullable().notRequired(),
        emailTemplateId: yup.string().when('messageType', {
            is: () => IS_EMAIL_CHANNEL,
            then: schema =>
                schema
                    .required('validation.required')
                    // Safety nets behind the picker filter (mirror backend preflight): coupon
                    // campaigns fill a dynamic-link button; message campaigns have no
                    // destination to supply.
                    .test(
                        'dynamic-link-required',
                        'TemplateLibrary.dynamicLinkRequired',
                        value =>
                            !value ||
                            messageType === MESSAGE_TYPE.MESSAGE ||
                            selectedEmailTemplateRef.current?.isLinkCapable !== false,
                    )
                    .test(
                        'dynamic-link-not-supported',
                        'TemplateLibrary.dynamicLinkNotSupported',
                        value =>
                            !value ||
                            messageType !== MESSAGE_TYPE.MESSAGE ||
                            selectedEmailTemplateRef.current?.isLinkCapable !== true,
                    ),
            otherwise: schema => schema.notRequired(),
        }),
        coverPhotoEnabled: yup.boolean(),
        coverPhotoUrl: yup.string().when('coverPhotoEnabled', {
            is: val => !!val && IS_WHATSAPP,
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.nullable(),
        }),
        whatsAppTemplateId: yup.string().nullable().notRequired(),
        whatsAppTemplateRegistryId: yup.string().when('channelId', {
            is: val => val && channelType === CHANNEL.WHATSAPP_BUSINESS,
            then: schema =>
                schema
                    .required('validation.required')
                    .test(
                        'wa-link-required',
                        'TemplateLibrary.noLinkButton',
                        value =>
                            !value ||
                            messageType === MESSAGE_TYPE.MESSAGE ||
                            selectedWhatsAppTemplateRef.current?.isLinkCapable !== false,
                    )
                    .test(
                        'wa-link-not-supported',
                        'TemplateLibrary.linkButtonNotSupported',
                        value =>
                            !value ||
                            messageType !== MESSAGE_TYPE.MESSAGE ||
                            selectedWhatsAppTemplateRef.current?.isLinkCapable !== true,
                    ),
            otherwise: schema => schema.notRequired(),
        }),
        couponTemplateId: yup.string().when('messageType', {
            is: messageType => messageType === MESSAGE_TYPE.COUPON,
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
    });

    const resolver = useYupValidationResolver(CampaignSchema);

    const {
        getValues,
        setValue,
        control,
        reset,
        watch,
        trigger,
        handleSubmit,
        formState: { isSubmitting, isValid, isDirty },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            title: '',
            message: '',
            subject: '',
            channelId: '',
            labels: [],
            scheduledTime: '',
            coverPhotoEnabled: false,
            coverPhotoUrl: '',
            messageType: MESSAGE_TYPE.MESSAGE,
            whatsAppTemplateId: '',
            whatsAppTemplateRegistryId: '',
            couponTemplateId: '',
            emailTemplateId: '',
        },
    });

    const emailTemplateIdValue = watch('emailTemplateId');
    useEffect(() => {
        selectedEmailTemplateRef.current =
            (libraryTemplates || []).find(item => item.id === emailTemplateIdValue) || null;
        trigger('emailTemplateId');
    }, [emailTemplateIdValue, libraryTemplates, trigger]);

    const whatsAppTemplateRegistryIdValue = watch('whatsAppTemplateRegistryId');
    useEffect(() => {
        selectedWhatsAppTemplateRef.current =
            (libraryTemplates || []).find(item => item.id === whatsAppTemplateRegistryIdValue) || null;
        // Link-capability validation depends on the selected template's metadata.
        trigger('whatsAppTemplateRegistryId');
    }, [whatsAppTemplateRegistryIdValue, libraryTemplates, trigger]);

    useEffect(() => {
        const isNewCampaign = modal.type === ModalType.Create;
        if (!isNewCampaign) {
            setValue('title', selectedCampaign?.title || '', { shouldValidate: true });
            setValue('message', selectedCampaign?.message || '', { shouldValidate: true });
            setValue('subject', selectedCampaign?.subject || '', { shouldValidate: true });
            setValue('coverPhotoEnabled', !!selectedCampaign?.coverPhotoEnabled, { shouldValidate: true });
            setValue('coverPhotoUrl', selectedCampaign?.coverPhotoUrl || '', { shouldValidate: true });
            setValue('emailTemplateId', selectedCampaign?.emailTemplateId || '', { shouldValidate: true });
            // Channels are team-owned now; a campaign channel is valid as long as it exists
            const campaignChannelExists = channels.some(c => c.id === selectedCampaign?.channelId);
            setValue(
                'channelId',
                selectedCampaign?.channelId === CHANNEL.ARCHIVED
                    ? 'ARCHIVED'
                    : campaignChannelExists
                      ? selectedCampaign?.channelId
                      : '',
                { shouldValidate: true },
            );
            setValue('whatsAppTemplateId', selectedCampaign?.whatsAppTemplateId || '', { shouldValidate: true });
            setValue('whatsAppTemplateRegistryId', selectedCampaign?.whatsAppTemplateRegistryId || '', {
                shouldValidate: true,
            });
            setValue('labels', selectedCampaign?.labels.map(l => l.id) || []);
            setValue('scheduledTime', new Date(selectedCampaign?.scheduledTime), {
                shouldValidate: true,
            });
            if (selectedCampaign?.couponTemplateId) {
                setValue('couponTemplateId', selectedCampaign?.couponTemplateId, { shouldValidate: true });
                setValue('messageType', MESSAGE_TYPE.COUPON, { shouldValidate: true });
                setMessageType(MESSAGE_TYPE.COUPON);
            }
            const channel = channels.find(c => c.id === selectedCampaign?.channelId);
            setChannelType(channel?.type || CHANNEL.SMS);
        }
    }, [selectedCampaign, channels, setValue, modal.type]);

    const onCloseModal = () => {
        setModal({ open: false, type: ModalType.Create });
        setSelectedCampaign(null);
        reset();
    };

    const getContactsNumber = async () => {
        const values = getValues();
        try {
            const response = await campaignsService.getSelectedContactsNumber({
                ...selectedCampaign,
                ...values,
                scheduledTime: isDate(values.scheduledTime)
                    ? new Date(values.scheduledTime).toISOString()
                    : new Date().toISOString(),
                labels: values.labels.length > 0 ? labels.filter(label => values.labels.includes(label.id)) : [],
                ...(modal.type === ModalType.Edit && { id: selectedCampaign?.id }),
            });
            setContactsNumber(response?.count);
            contactsNumberRef.current = response?.count;
            trigger('labels');
        } catch (_) {
            logger.error("Couldn't get contacts number");
        }
    };

    const onSaveCampaign = async () => {
        const values = getValues();

        let newCampaign;
        try {
            newCampaign = await campaignsService.createOrUpdateCampaign({
                ...selectedCampaign,
                ...values,
                message:
                    (messageType === MESSAGE_TYPE.MESSAGE || messageType === MESSAGE_TYPE.COUPON) && !IS_EMAIL_CHANNEL
                        ? values.message
                        : '',
                couponTemplateId: messageType === MESSAGE_TYPE.COUPON ? values.couponTemplateId : null,
                emailTemplateId: IS_EMAIL_CHANNEL ? values.emailTemplateId : null,
                whatsAppTemplateId: IS_WHATSAPP_BUSINESS ? null : values.whatsAppTemplateId || null,
                whatsAppTemplateRegistryId: IS_WHATSAPP_BUSINESS ? values.whatsAppTemplateRegistryId : null,
                scheduledTime: new Date(values.scheduledTime).toISOString(),
                labels: labels.filter(label => values.labels.includes(label.id)),
                ...(modal.type === ModalType.Edit && { id: selectedCampaign?.id }),
            });
        } catch (error) {
            handleApiError(error, `alertMessages.${modal.type === ModalType.Create ? 'createFail' : 'editFail'}`);
            return;
        }

        if (modal.type === ModalType.Create) {
            dispatch(campaignsActions.get(0, rowsPerPage));
        } else {
            const campaignsCopy = data.slice();
            const updatedCampaignIndex = campaignsCopy.findIndex(c => c.id === selectedCampaign?.id);
            if (updatedCampaignIndex !== -1 && newCampaign) {
                campaignsCopy[updatedCampaignIndex] = newCampaign;
                dispatch(campaignsActions.setCampaigns(campaignsCopy));
            }
            setSelectedCampaign(newCampaign);
        }
        dispatch(
            alertActions.success(
                t(`alertMessages.${modal.type === ModalType.Create ? 'createSuccess' : 'editSuccess'}`),
            ),
        );
        onCloseModal();
    };

    const isView = modal.type === ModalType.View;

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={onCloseModal}
            isDirty={isDirty}
            title={
                modal.type === ModalType.Create
                    ? t('CampaignsPage.createTitle')
                    : isView
                      ? t('CampaignsPage.viewTitle')
                      : t('CampaignsPage.editTitle')
            }
            onPrimaryAction={isView ? null : handleSubmit(onSaveCampaign)}
            primaryActionText={
                modal.type === ModalType.Create ? t('buttons.cheduleCampaign') : t('buttons.saveChanges')
            }
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
            onAdditionalAction={modal.type !== ModalType.Edit ? null : () => onDelete(selectedCampaign)}
            additionalTextButton={modal.type !== ModalType.Edit ? null : t('buttons.delete')}
        >
            <Content>
                <FormProvider {...{ setValue, watch, control, trigger, selectedCampaign }}>
                    <PrimaryFields
                        isView={isView}
                        isEdit={modal.type === ModalType.Edit}
                        channels={channels}
                        setChannelType={setChannelType}
                        getContactsNumber={getContactsNumber}
                        user={user}
                    />
                    <ScheduledTimeField isView={isView} />
                    <LabelsFields
                        isView={isView}
                        channelType={channelType}
                        getContactsNumber={getContactsNumber}
                        contactsNumber={contactsNumber}
                        labels={labels}
                    />
                    <ContentSource
                        channelType={channelType}
                        isView={isView}
                        messageType={messageType}
                        setMessageType={setMessageType}
                    />
                </FormProvider>
            </Content>
        </Modal>
    );
};

export default EditModal;
